import { itemsAtom, optionGroupsAtom } from "@/atoms"
import { showToast } from "@/components/feedback"
import { Button, Card, Icon, QuantityStepper, SectionHeader, Text } from "@/components/ui"
import { useBrowsingChannelCatalog } from "@/hooks/use-browsing-channel-catalog"
import { useOptimisticCart } from "@/hooks/use-optimistic-cart"
import type { LoyaltyReward } from "@/lib/discount-evaluation/types"
import { buildFreeProductCartItem } from "@/lib/free-product-cart-item"
import { formatPoints } from "@/lib/format-points"
import {
  emptyRewardsState,
  redeemableUnits,
  reservedRewardPoints,
  rewardLineIndex,
  type RewardRedemption,
} from "@/lib/loyalty-rewards"
import { StarIcon } from "@/icons"
import { useAtom, useAtomValue } from "jotai"
import { v4 as uuid } from "uuid"
import { checkoutAtom } from "../../state"

/**
 * Items the customer can take with points. Redeeming puts the item in the bag
 * when it is not there yet and an ITEM discount on its line; the balance shown
 * is what is left after the picks. Loyalty re-checks every redemption when the
 * order is placed.
 */
export function RewardsSection() {
  const [state, setState] = useAtom(checkoutAtom)
  const items = useAtomValue(itemsAtom)
  const optionGroups = useAtomValue(optionGroupsAtom)
  const { itemOffers, categories } = useBrowsingChannelCatalog()
  const { cart, addItem, removeItem, updateItemQuantity } = useOptimisticCart()
  const rewards = state.rewards ?? emptyRewardsState

  if (rewards.available.length === 0) return null

  const left = Math.max(0, rewards.balance - reservedRewardPoints(rewards))

  function setPick(rewardId: string, next: RewardRedemption | null) {
    setState((prev) => {
      const current = prev.rewards ?? emptyRewardsState
      const others = current.redeemed.filter((pick) => pick.rewardId !== rewardId)
      return { ...prev, rewards: { ...current, redeemed: next ? [...others, next] : others } }
    })
  }

  function redeemFirst(reward: LoyaltyReward) {
    if (rewardLineIndex(cart, reward) >= 0) {
      setPick(reward.id, { rewardId: reward.id, quantity: 1 })
      return
    }
    const resolved = buildFreeProductCartItem(
      { itemId: reward.item.itemId, externalCode: reward.item.externalCode, name: reward.item.name },
      { items, itemOffers, optionGroups, categories },
      uuid(),
    )
    if (!resolved.ok) {
      showToast.error(
        resolved.reason === "requires-options"
          ? "Esse item tem opções a escolher. Adicione ele pelo cardápio para resgatar."
          : "Não conseguimos colocar esse item na sacola agora. Tente de novo em instantes.",
      )
      return
    }
    const claimedLineId = addItem(resolved.cartItem)
    setPick(reward.id, { rewardId: reward.id, quantity: 1, claimedLineId })
  }

  function change(reward: LoyaltyReward, pick: RewardRedemption, delta: 1 | -1) {
    const line = cart.items[rewardLineIndex(cart, reward, pick.claimedLineId)]
    const quantity = pick.quantity + delta
    if (delta > 0 && line && line.quantity < quantity) updateItemQuantity(line.id, quantity)
    if (delta < 0 && line && pick.claimedLineId === line.id) {
      if (quantity <= 0) removeItem(line.id)
      else updateItemQuantity(line.id, quantity)
    }
    setPick(reward.id, quantity > 0 ? { ...pick, quantity } : null)
  }

  return (
    <div>
      <SectionHeader
        title="Resgate com pontos"
        description={<span aria-live="polite">Você tem {formatPoints(Math.floor(left), "pontos")} para resgatar.</span>}
      />
      <ul className="flex flex-col gap-2" aria-label="Itens para resgatar com pontos">
        {rewards.available.map((reward) => {
          const pick = rewards.redeemed.find((entry) => entry.rewardId === reward.id)
          const canAddOne = redeemableUnits(reward, rewards) > (pick?.quantity ?? 0)
          return (
            <Card key={reward.id} as="li" padding="md" className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <Icon as={StarIcon} size="xl" tone="secondary" />
                <div className="min-w-0">
                  <Text weight="semibold" tone="default" className="break-words">
                    {reward.item.name || "Item"}
                  </Text>
                  <Text size="sm" tone="muted">
                    {formatPoints(Math.floor(reward.pointsCost), "pontos")}
                    {reward.maxPerOrder > 0 ? ` · até ${reward.maxPerOrder} por pedido` : ""}
                  </Text>
                </div>
              </div>
              {pick ? (
                <QuantityStepper
                  variant="split"
                  size="md"
                  value={pick.quantity}
                  onIncrement={() => change(reward, pick, 1)}
                  onDecrement={() => change(reward, pick, -1)}
                  incrementDisabled={!canAddOne}
                  label={`Resgate de ${reward.item.name}`}
                  incrementLabel={`Resgatar mais um ${reward.item.name}`}
                  decrementLabel={`Resgatar um ${reward.item.name} a menos`}
                  className="self-end sm:self-center"
                />
              ) : (
                <Button
                  variant="outline"
                  tone="primary"
                  size="sm"
                  onClick={() => redeemFirst(reward)}
                  disabled={!canAddOne}
                  className="self-end sm:self-center"
                >
                  {canAddOne ? "Resgatar" : "Pontos insuficientes"}
                </Button>
              )}
            </Card>
          )
        })}
      </ul>
    </div>
  )
}
